import { useTranslation } from "react-i18next"
import { TrashSimple } from "@phosphor-icons/react"
import { formatDate } from "date-fns"
import { useUser } from "@/apis/bff"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardBody,
  CardBodyContent,
  CardBodyHeader,
  CardBodyHeaderTitle,
  CardHeader,
  CardHeaderTitle,
} from "@/components/ui/card.tsx"
import { VISUAL_DATE_FORMAT } from "@/components/ui/date-picker"
import { TooltipContent, TooltipCustom, TooltipTrigger } from "@/components/ui/tooltip"
import { formatDateString } from "@/lib/format-date"
import { formatSocialSecurityNumber } from "@/lib/format-social-security-number"
import { checkIfSMSEnabled } from "@/lib/sms-enabled-phone-number"
import { getCleanPhoneNumber } from "@/lib/utils"
import { NoChannelContent } from "@/modules/inbound-wizard/components/no-channel-content"
import { usePhoneNumberCountryList } from "@/modules/patients/components/phoneNumber/hooks/usePhoneNumberCountryList"
import { PatientData } from "@/modules/patients/detail/store/usePatientStore"
import { getTranslatedCountry } from "@/modules/patients/libs/get-translated-country"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { chPatientByIdQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientByIdQuery.graphql"
import { useChDeletePatientSocialSecurityNumberMutation } from "@/modules/patients/services/ch/useChDeletePatientSocialSecurityNumber"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { ContactChannel, Country, Gender, Language } from "@/types/__generated__/union-types"

type EmailVerification = NonNullable<chPatientByIdQuery$data["patientById"]>["emailVerification"]

type Address = NonNullable<PatientFragment$data["address"]>

interface ContactProps {
  patientId: string
  isEditMode?: boolean
  firstName: string
  lastName: string
  gender: Gender
  birthDate: string
  country: Country
  primaryLanguage?: Language | null
  secondaryLanguages?: readonly (Language | null)[] | null
  address?: Address | null | undefined
  socialSecurityNumber?: string | null
  emailAddress?: string | undefined | null
  phoneNumber?: string | undefined | null
  contactChannel?: ContactChannel
  emailVerification?: EmailVerification
  appAccount: NonNullable<PatientData>["account"]
  children?: React.ReactNode
}

const Contact = ({
  patientId,
  isEditMode,
  firstName,
  lastName,
  gender,
  birthDate,
  primaryLanguage,
  secondaryLanguages,
  address,
  socialSecurityNumber,
  emailAddress,
  phoneNumber,
  country,
  contactChannel,
  emailVerification,
  appAccount,
  children,
}: ContactProps) => {
  const { t } = useTranslation()
  const { getCountryByPhoneNumber } = usePhoneNumberCountryList("")
  const formattedPhoneNumber = phoneNumber ?? ""
  const phoneNumberCountry = getCountryByPhoneNumber(formattedPhoneNumber)
  const isSMSNumberValid = checkIfSMSEnabled(phoneNumberCountry?.code ?? "", formattedPhoneNumber)
  const { accountVerification } = appAccount ?? {}
  const { emailAddress: appEmail, status: appStatus } = accountVerification ?? {}
  const { isOneOf } = useUser()
  const confirm = useConfirmStore((state) => state.confirm)
  const [deleteSocialSecurityNumber] = useChDeletePatientSocialSecurityNumberMutation()
  const formattedSocialSecurityNumber = formatSocialSecurityNumber(socialSecurityNumber)
  const canDeleteSocialSecurityNumber =
    !!isEditMode && isOneOf(["CustomerServiceCenter", "MedicalSupervisor"]) && !!formattedSocialSecurityNumber

  const handleDeleteSocialSecurityNumber = async () => {
    const isConfirmed = await confirm({
      title: "patient.information.deleteAhvNumber",
      content: "patient.information.deleteAhvNumberConfirmation",
      confirmText: "common.button.delete",
      cancelText: "common.button.cancel",
    })

    if (!isConfirmed) {
      return
    }

    deleteSocialSecurityNumber({ variables: { input: { patientId } } })
  }

  return (
    <Card className="animate-fade-in-top w-full" data-testid={"patient-information-contact"}>
      <CardHeader>
        <div className="flex w-full items-center justify-between gap-4">
          <CardHeaderTitle data-testid="card-header-title-content">{t("common.contact")}</CardHeaderTitle>
          {children}
        </div>
      </CardHeader>
      <CardBody>
        <CardBodyHeader>
          <CardBodyHeaderTitle>{t("common.address")}</CardBodyHeaderTitle>
        </CardBodyHeader>
        <CardBodyContent>
          <div className="flex-1">
            <p className="text-on-surface-subdued text-xs">{t("common.name")}</p>
            <p className="text-on-surface text-sm" data-testid="patient-name">
              {firstName} {lastName}
            </p>
          </div>
          <div className="flex-1">
            <p className="text-on-surface-subdued text-xs">{t("common.dateOfBirth")}</p>
            <p className="text-on-surface text-sm" data-testid="patient-date-of-birth">
              {formatDate(birthDate, VISUAL_DATE_FORMAT)}
            </p>
          </div>
          <div className="flex-1">
            <p className="text-on-surface-subdued text-xs" data-testid="gender-label">
              {t("common.gender")}
            </p>
            <p className="text-on-surface text-sm" data-testid="gender-value-label">
              {t(`common.genderList.${gender}`)}
            </p>
          </div>
        </CardBodyContent>
        <CardBodyContent>
          <div className="flex-1" data-testid="patient-address">
            <p className="text-on-surface-subdued text-xs">{t("common.home")}</p>
            <p className="text-on-surface text-sm" data-testid="house-number-street-label">
              {address?.houseNumberStreet}
            </p>
            <p className="text-on-surface text-sm" data-testid="postal-code-and-city-label">
              {address?.postalCode} {address?.city}
            </p>
            <p className="text-on-surface text-sm" data-testid="country-label">
              {getTranslatedCountry(address?.country)}
            </p>
          </div>
          {country === "CH" && (
            <>
              <div className="flex-1" data-testid="patient-ahv-wrapper">
                <p className="text-on-surface-subdued text-xs" data-testid="patient-ahv-label">
                  {t("patient.information.ahvNumber")}
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-on-surface text-sm" data-testid="patient-ahv-number">
                    {formattedSocialSecurityNumber || "-"}
                  </p>
                  {canDeleteSocialSecurityNumber && (
                    <Button
                      data-testid="delete-patient-ahv-number-button"
                      variant="circle"
                      className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center p-1"
                      onClick={handleDeleteSocialSecurityNumber}>
                      <TrashSimple className="text-on-surface-subdued" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex-1" />
            </>
          )}
        </CardBodyContent>
      </CardBody>
      <CardBody>
        <CardBodyHeader>
          <div className="flex items-center gap-4" data-testid="contact-channel">
            <CardBodyHeaderTitle>{t(`patient.information.contact.${contactChannel}`)}</CardBodyHeaderTitle>
          </div>
        </CardBodyHeader>
        <CardBodyContent>
          {contactChannel === "NO_CHANNEL" && (
            <div className="-mt-4 w-full">
              <NoChannelContent />
            </div>
          )}

          {contactChannel === "APP" && (
            <div className="flex w-full">
              {appStatus === "VERIFIED" ? (
                <>
                  <div className="flex-1">
                    <p className="text-on-surface-subdued text-xs">{`${t("patient.information.app.username")} / ${t("patient.information.app.appEmail")}`}</p>
                    <p className="text-on-surface text-sm">{appEmail}</p>
                  </div>
                  <div className="flex-1">
                    <p className="text-on-surface-subdued text-xs">{t("patient.information.appPhoneNumber")}</p>
                    <p className="text-on-surface text-sm">{phoneNumber}</p>
                  </div>
                </>
              ) : (
                <Banner type="inline" variant="warning">
                  {t("patient.information.contact.signUpMustBeCompleted")}
                </Banner>
              )}
            </div>
          )}

          {country === "CH" && contactChannel === "EMAIL_AND_SMS" && (
            <div className="flex w-full flex-col justify-between gap-4 @md/patient-layout:flex-row @lg/patient-layout:flex-col @xl/patient-layout:flex-row">
              <div className="w-full min-w-0 @md/patient-layout:w-1/2 @lg/patient-layout:w-full @xl/patient-layout:w-1/2">
                <div className="text-on-surface-subdued text-xs leading-none font-normal tracking-tight">
                  {t("common.email")}
                </div>
                <TooltipCustom>
                  <TooltipTrigger asChild>
                    <div className="text-on-surface truncate text-sm leading-tight font-normal">{emailAddress}</div>
                  </TooltipTrigger>
                  <TooltipContent className="break-all">
                    <p>{emailAddress}</p>
                  </TooltipContent>
                </TooltipCustom>
                {!emailVerification?.verifiedAt && (
                  <Banner type="inline" variant="critical" className="mt-4 mr-4">
                    <div className="flex w-full flex-col">
                      <p>
                        {t("patient.information.contact.emailNotConfirmed")}{" "}
                        {(emailVerification?.sentAt || emailVerification?.createdAt) &&
                          t("patient.information.contact.lastSent", {
                            sentAt: formatDateString(emailVerification?.sentAt ?? emailVerification?.createdAt),
                          })}
                      </p>
                    </div>
                  </Banner>
                )}
              </div>
              <div className="w-full @md/patient-layout:w-1/2 @lg/patient-layout:w-full @xl/patient-layout:w-1/2">
                <div className="text-on-surface-subdued text-xs leading-none font-normal tracking-tight">
                  {t("common.phoneNumber")}
                </div>
                <div className="text-on-surface text-sm leading-tight font-normal">
                  {getCleanPhoneNumber(phoneNumber)}
                </div>
                {!isSMSNumberValid && (
                  <div className="mt-4">
                    <Banner variant="warning" type="inline">
                      <span className="text-inherit">{t("common.fieldErrorList.notSMSSupported")}</span>
                    </Banner>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardBodyContent>
      </CardBody>
      <CardBody>
        <CardBodyHeader>
          <div className="flex items-center gap-4">
            <CardBodyHeaderTitle>{t("common.languages")}</CardBodyHeaderTitle>
          </div>
        </CardBodyHeader>
        <CardBodyContent>
          <div className="flex w-full justify-between">
            <div className="w-1/2">
              <div className="text-on-surface-subdued text-xs leading-none font-normal tracking-tight">
                {t("patient.information.primaryLanguage")}
              </div>
              <div className="text-on-surface text-sm leading-tight font-normal capitalize">
                {primaryLanguage?.toLowerCase()}
              </div>
            </div>
            <div className="w-1/2">
              <div className="text-on-surface-subdued text-xs leading-none font-normal tracking-tight">
                {t("patient.information.secondaryLanguages")}
              </div>
              <div className="text-on-surface text-sm leading-tight font-normal capitalize">
                {secondaryLanguages ? secondaryLanguages.join(", ").toLowerCase() : null}
              </div>
            </div>
          </div>
        </CardBodyContent>
      </CardBody>
    </Card>
  )
}
export { Contact }
